import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import dayjs from 'dayjs';

import eCoachVMSApi from '../Api';
import { getServiceDefectData } from '../api/getDailySerivceDefectData';
import { getDailyServiceData } from '../api/getDailyServiceData';
import { toUTCDateFormat } from '../utils/dateUtils';
import { transformServicesToEvents } from '../utils/serviceDataTransform';

const { baseURL } = eCoachVMSApi();

const multipleServicesStyle = {
    font: '#ffffff',
    border_color: '#3B82F6',
    Tooltip: 'This Service contains multiple services',
    background_color: '#1E3A8A',
    service_id: 99,
    service_type: 'Multiple'
};

// --- Helper to get Initial Dates ---
const getInitialDates = () => {
    const today = dayjs();
    return {
        initialStartDate: today,
        initialActualStartDate: today
    };
};
// --- End Helper ---

const { initialStartDate, initialActualStartDate } = getInitialDates();

// --- Default Initial Tabs ---
export const defaultInitialTabs = [
    { key: '1', label: 'Newest', children: null, closable: false },
    { key: '2', label: 'Planning', children: null, closable: false },
    { key: '3', label: 'Show All', children: null, closable: false }
];
// --- End Default Initial Tabs ---

// --- Default Filter Values ---
export const defaultFilters = {
    selectedStatuses: [],
    selectedServiceTypes: [],
    selectedVehicles: [],
    selectedStatutory: false,
    selectedRoadWorthy: false,
    selectedOffRoad: false,
    selectedScheduled: false,
    selectedUnscheduled: false,
    selectedDefect: false,
    selectedNoStartDate: false,
    selectedSeverity: 'Choose',
    selectedSupplier: '0'
};
// --- End Default Filter Values ---

// --- Async Thunks ---
export const fetchInitialCalendarData = createAsyncThunk('serviceCalendar/fetchInitialData', async (_, { rejectWithValue }) => {
    try {
        const data = await getServiceDefectData();
        if (!data) throw new Error('No data received from getServiceDefectData');

        // Process data needed for filters and display
        const serviceStatuses =
            data.serviceStatus?.map((curr) => ({
                id: curr.order,
                fullStatus: curr.StatusType, // Keep original full status string for potential API filtering
                type: curr.StatusType?.slice(3).trim() || 'Unknown', // For display
                backgroundColor: curr.background_color,
                borderColor: curr.border_color,
                textColor: curr.text_color,
                tooltipText: curr.StatusTooltip || ''
            })) || [];

        const serviceTypeData =
            data.serviceTypes?.map((curr) => ({
                id: curr.service_id,
                type: curr.service_type?.trim() || 'Unknown', // Name for display and potentially filtering
                backgroundColor: curr.bgcolor,
                borderColor: curr.border,
                textColor: curr.font,
                tooltipText: curr.Tooltip || ''
            })) || [];

        // Resources for DayPilot rows
        const resourceData =
            data.vehicles
                ?.map((curr) => ({
                    id: `${curr.vehicle_id}`,
                    name: curr.vehicle_reg,
                    vehicleMake: curr.vehicle_make,
                    carName: curr.car_name,
                    vehicleReg: curr.vehicle_reg,
                    serviceDue: curr.next_message
                }))
                .sort((a, b) => a.name.localeCompare(b.name)) || [];

        // Separate detailed vehicle list for filter dropdown
        const vehicleData =
            data.vehicles
                ?.map((curr) => ({
                    vehicleId: curr.vehicle_id,
                    carName: curr.car_name,
                    vehicleReg: curr.vehicle_reg
                }))
                .sort((a, b) => (a.vehicleReg || '').localeCompare(b.vehicleReg || '')) || [];

        const severityData =
            data.severity?.map((curr) => ({
                severityType: curr.Severity,
                backgroundColor: curr.bgcolor,
                textColor: curr.fgcolor,
                tooltipText: curr.Tooltip || ''
            })) || [];

        // Add "Choose" option at index 0 for severity filter dropdown
        severityData.unshift({
            severityType: 'Choose',
            backgroundColor: '#FFFFFF',
            textColor: '#000000',
            tooltipText: 'All Severities'
        });

        const supplierData = data?.suppliers?.map((supplier) => ({
            supplierId: supplier.id,
            supplierName: supplier.name
        }));
        supplierData.unshift({ supplierId: '0', supplierName: 'All Suppliers' });

        return {
            resources: resourceData,
            statusInfo: serviceStatuses,
            serviceInfos: serviceTypeData,
            vehicles: vehicleData,
            severities: severityData,
            suppliers: supplierData
        };
    } catch (error) {
        console.error('Error fetching initial calendar data:', error);
        return rejectWithValue(error.message || 'Failed to fetch initial data');
    }
});

export const fetchServices = createAsyncThunk('serviceCalendar/fetchServices', async (_, { getState, rejectWithValue }) => {
    const { serviceCalendar } = getState();
    const {
        viewActualStartDate,
        viewStartDate,
        viewIntervalMonths,
        viewDateCompareOperator,
        dailyViewActualStartDate,
        dailyViewStartDate,
        dailyViewIntervalMonths,
        dailyViewDateCompareOperator,
        filtersByTab,
        calendarFilters, // <-- Get calendarFilters state
        activeTabKey,
        search // Top-level search state
    } = serviceCalendar;

    // --- Determine which filters to use ---
    let filters;
    let startDateParam, intervalMonthsParam, comparatorParam;

    if (activeTabKey) {
        // Use Daily Service parameters
        filters = filtersByTab[activeTabKey] || defaultFilters;
        startDateParam = dailyViewActualStartDate;
        intervalMonthsParam = dailyViewIntervalMonths;
        comparatorParam = dailyViewDateCompareOperator;
    } else {
        // Use Calendar parameters
        filters = calendarFilters || defaultFilters;
        startDateParam = viewActualStartDate;
        intervalMonthsParam = viewIntervalMonths;
        comparatorParam = viewDateCompareOperator;
    }
    // --- End Determine filters ---

    // --- 2. Construct Filter Query String ---
    let filterParts = [];

    // Handle date parameters differently for daily vs calendar
    if (activeTabKey) {
        // Daily Service - use exact date and compare parameter
        if (filters.selectedNoStartDate) {
            filterParts.push(`start_date=${null}`);
        } else {
            const exactDate = toUTCDateFormat(dayjs(startDateParam));
            filterParts.push(`start_date=${exactDate}`);

            // Add compare parameter for daily
            let compareValue;
            switch (comparatorParam) {
                case 'bigger':
                    compareValue = 'gt';
                    break;
                case 'smaller':
                    compareValue = 'lt';
                    break;
                case 'equals':
                default:
                    compareValue = 'eq';
                    break;
            }
            filterParts.push(`compare=${compareValue}`);
        }
    } else {
        // Calendar - use date range calculation (existing logic)
        let startDateService, endDateService;
        const actualStartMoment = dayjs(startDateParam);
        const startMoment = dayjs(viewStartDate);

        if (comparatorParam === 'equals') {
            startDateService = toUTCDateFormat(actualStartMoment);
            endDateService = toUTCDateFormat(actualStartMoment.add(intervalMonthsParam, 'month'));
        } else if (comparatorParam === 'bigger') {
            startDateService = toUTCDateFormat(startMoment);
            endDateService = toUTCDateFormat(startMoment.add(intervalMonthsParam, 'month'));
        } else if (comparatorParam === 'smaller') {
            startDateService = toUTCDateFormat(actualStartMoment.subtract(intervalMonthsParam, 'month'));
            endDateService = toUTCDateFormat(actualStartMoment);
        } else {
            startDateService = toUTCDateFormat(startMoment);
            endDateService = toUTCDateFormat(startMoment.add(intervalMonthsParam, 'month'));
        }

        if (!startDateService || !endDateService) {
            return rejectWithValue('Invalid date range parameters');
        }

        if (filters.selectedNoStartDate) {
            filterParts.push(`start_date=${null}`);
            filterParts.push(`end_date=${null}`);
        } else {
            filterParts.push(`start_date=${startDateService}`);
            filterParts.push(`end_date=${endDateService}`);
        }
    }

    if (filters.selectedStatutory) filterParts.push(`statutory=true`);
    if (filters.selectedRoadWorthy) filterParts.push(`roadworthy=true`);
    if (filters.selectedScheduled) filterParts.push(`scheduled=true`);
    if (filters.selectedUnscheduled) filterParts.push(`unscheduled=true`);
    if (filters.selectedDefect) filterParts.push(`defect=true`);

    // Add multi-select values (adjust param names & value format based on API)
    if (filters.selectedStatuses?.length > 0) filterParts.push(`status_id=${filters.selectedStatuses.join(',')}`);
    if (filters.selectedServiceTypes?.length > 0) filterParts.push(`service_ids=${filters.selectedServiceTypes.join(',')}`);
    if (filters.selectedVehicles?.length > 0) filterParts.push(`vehicle_ids=${filters.selectedVehicles.join(',')}`);
    if (filters.selectedSeverity && filters.selectedSeverity !== 'Choose') filterParts.push(`severity=${filters.selectedSeverity}`);
    if (filters.selectedSupplier && filters.selectedSupplier !== '0') filterParts.push(`supplier_id=${filters.selectedSupplier}`);

    // Use top-level 'search' state (applies to both contexts)
    if (search && search.trim() !== '') {
        filterParts.push(`search=${search.trim()}`);
    }

    const filterString = filterParts.join('&');

    // --- 3. Make API Call ---
    try {
        const res = await getDailyServiceData(filterString);
        if (!res) throw new Error('No data received from getDailyServiceData');
        return res;
    } catch (error) {
        console.error('Error fetching calendar events:', error);
        return rejectWithValue(error.message || 'Failed to fetch events');
    }
});

export const moveEvent = createAsyncThunk('serviceCalendar/moveEvent', async ({ eventData, serviceType, moveType }, { rejectWithValue }) => {
    try {
        let response;
        if (serviceType === 'autoScheduled' && !moveType) {
            response = await baseURL.post('previewMove', {
                ...eventData,
                move: 'all'
            });
        } else if (serviceType === 'autoScheduled' && moveType) {
            response = await baseURL.post('moveService', {
                ...eventData,
                move: moveType
            });
        } else {
            response = await baseURL.post('moveService', {
                ...eventData
            });
        }

        return response.data;
    } catch (error) {
        if (error.response.status === 409) {
            // ✅ return the actual response data on conflict
            return error.response.data;
        }
        return rejectWithValue(error.response.data);
    }
});

export const fetchYearViewData = createAsyncThunk('serviceCalendar/fetchYearViewData', async ({ vehicleId, year }, { rejectWithValue }) => {
    try {
        // Construct date range for the entire year
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;

        // Build filter string for year view
        const filterParts = [`start_date=${startDate}`, `end_date=${endDate}`, `vehicle_ids=${vehicleId}`];

        const filterString = filterParts.join('&');

        const res = await getDailyServiceData(filterString);
        if (!res) throw new Error('No data received from getDailyServiceData');

        return res;
    } catch (error) {
        console.error('Error fetching year view data:', error);
        return rejectWithValue(error.message || 'Failed to fetch year view data');
    }
});

// --- Initial State Definition ---
const initialState = {
    services: [],
    resources: [],
    events: [],
    statusInfo: [],
    serviceInfos: [],
    vehicles: [], // For filter dropdown
    severities: [], // For filter dropdown
    suppliers: [], // For filter dropdown
    filteredResourceIds: null, // to track which vehicles should be shown on calendar
    yearViewEvents: [], // store year view specific events

    // Calendar specific view state
    viewStartDate: initialStartDate.toISOString(),
    viewActualStartDate: initialActualStartDate.toISOString(),
    viewIntervalMonths: 2,
    viewDateCompareOperator: 'bigger',
    calendarIsDateMode: false,
    calendarStoredDateModeValues: {
        date: initialActualStartDate.toISOString(),
        compare: 'bigger'
    },
    calendarStoredMonthYearValues: {
        month: initialActualStartDate.month() + 1,
        year: initialActualStartDate.year()
    },

    // Daily service specific view state
    dailyViewStartDate: initialStartDate.toISOString(),
    dailyViewActualStartDate: initialActualStartDate.toISOString(),
    dailyViewIntervalMonths: 2,
    dailyViewDateCompareOperator: 'bigger',
    dailyIsDateMode: true,
    dailyStoredDateModeValues: {
        date: initialActualStartDate.toISOString(),
        compare: 'bigger'
    },
    dailyStoredMonthYearValues: {
        month: initialActualStartDate.month() + 1,
        year: initialActualStartDate.year()
    },

    filtersByTab: {},
    activeTabKey: null,

    calendarFilters: { ...defaultFilters },

    popups: {
        status: false,
        service: false,
        filter: false,
        yearView: false,
        eventDetails: false,
        vehicle: false,
        newEvent: false
    },
    eventPopupDetails: {
        event: {},
        position: {}
    },
    vehiclePopupDetails: {
        vehicle: {},
        position: {}
    },
    newEventPopupDetails: {
        vehicle: {},
        position: {}
    },

    yearViewSelectedVehicleId: null,
    yearViewSelectedVehicleDetail: '',

    loadingStatus: 'idle',
    eventMovingStatus: 'idle', // loading state for event moving
    yearViewLoadingStatus: 'idle', // loading state for year view
    error: null,
    search: ''
};
// --- End Initial State Definition ---

// --- Slice Definition ---
export const serviceCalendarSlice = createSlice({
    name: 'serviceCalendar',
    initialState,
    reducers: {
        updateCalendarView: (state, action) => {
            const { startDate, actualStartDate, intervalMonths, operator } = action.payload;
            if (startDate !== undefined) state.viewStartDate = startDate;
            if (actualStartDate !== undefined) state.viewActualStartDate = actualStartDate;
            if (intervalMonths !== undefined) state.viewIntervalMonths = intervalMonths;
            if (operator !== undefined) state.viewDateCompareOperator = operator;
        },

        updateDailyView: (state, action) => {
            const { startDate, actualStartDate, intervalMonths, operator } = action.payload;
            if (startDate !== undefined) state.dailyViewStartDate = startDate;
            if (actualStartDate !== undefined) state.dailyViewActualStartDate = actualStartDate;
            if (intervalMonths !== undefined) state.dailyViewIntervalMonths = intervalMonths;
            if (operator !== undefined) state.dailyViewDateCompareOperator = operator;
        },

        toggleViewMode: (state, action) => {
            const { page, isDateMode } = action.payload;

            if (page === 'calendar') {
                state.calendarIsDateMode = isDateMode;
            } else if (page === 'daily') {
                state.dailyIsDateMode = isDateMode;
            }
        },

        updateStoredDateModeValues: (state, action) => {
            const { page, values } = action.payload;

            if (page === 'calendar') {
                state.calendarStoredDateModeValues = {
                    ...state.calendarStoredDateModeValues,
                    ...values
                };
            } else if (page === 'daily') {
                state.dailyStoredDateModeValues = {
                    ...state.dailyStoredDateModeValues,
                    ...values
                };
            }
        },

        updateStoredMonthYearValues: (state, action) => {
            const { page, values } = action.payload;

            if (page === 'calendar') {
                state.calendarStoredMonthYearValues = {
                    ...state.calendarStoredMonthYearValues,
                    ...values
                };
            } else if (page === 'daily') {
                state.dailyStoredMonthYearValues = {
                    ...state.dailyStoredMonthYearValues,
                    ...values
                };
            }
        },

        // --- Tab Specific Reducers (for DailyService) ---
        setActiveTabKey: (state, action) => {
            state.activeTabKey = action.payload;
        },
        initializeTabFilters: (state, action) => {
            const { tabKey, filters } = action.payload;
            state.filtersByTab[tabKey] = { ...defaultFilters, ...(filters || {}) };
        },
        removeTabFilters: (state, action) => {
            const { tabKey } = action.payload;
            delete state.filtersByTab[tabKey];
        },
        // --- End Tab Specific Reducers ---

        // --- Calendar Specific Reducer ---
        initializeCalendarFilters: (state, action) => {
            const filters = action.payload || {};
            state.calendarFilters = { ...defaultFilters, ...filters };
        },

        //an action to set search value in the state
        setSearchValue: (state, action) => {
            state.search = action.payload;
        },

        // New action to update calendar resources based on vehicle filter
        updateCalendarResources: (state, action) => {
            const { selectedVehicleIds } = action.payload;
            state.filteredResourceIds = selectedVehicleIds;
        },

        // --- End Calendar Specific Reducer ---

        // --- Shared Filter Reducers ---
        updateFilters: (state, action) => {
            const { filterUpdate, tabKey } = action.payload; // tabKey is optional
            if (tabKey) {
                // Update DailyService tab filters
                if (!state.filtersByTab[tabKey]) {
                    state.filtersByTab[tabKey] = { ...defaultFilters };
                }
                state.filtersByTab[tabKey] = {
                    ...state.filtersByTab[tabKey],
                    ...filterUpdate
                };
            } else {
                // Update main ServiceCalendar filters
                state.calendarFilters = {
                    ...state.calendarFilters,
                    ...filterUpdate
                };
            }
        },
        resetFilters: (state, action) => {
            // payload can be { tabKey: '...' } or empty/undefined
            const { tabKey } = action.payload || {};
            if (tabKey) {
                // Reset DailyService tab filters
                state.filtersByTab[tabKey] = { ...defaultFilters };
            } else {
                // Reset main ServiceCalendar filters
                state.calendarFilters = { ...defaultFilters };
            }
        },
        // --- End Shared Filter Reducers ---

        // Consolidated Popup Visibility Reducer
        setPopupVisibility: (state, action) => {
            const { popupName, isVisible, vehicleId, vehicleDetail } = action.payload;
            if (state.popups[popupName] !== undefined) {
                state.popups[popupName] = isVisible;
            }
            // Handle year view sp ecific state
            if (popupName === 'yearView') {
                if (isVisible) {
                    state.yearViewSelectedVehicleId = vehicleId;
                    state.yearViewSelectedVehicleDetail = vehicleDetail;
                } else {
                    state.yearViewSelectedVehicleId = null;
                    state.yearViewSelectedVehicleDetail = '';
                }
            }
        },
        setEventPopupDetails: (state, action) => {
            state.eventPopupDetails = action.payload;
        },

        setVehiclePopupDetails: (state, action) => {
            state.vehiclePopupDetails = action.payload;
        },

        setNewEventPopupDetails: (state, action) => {
            state.newEventPopupDetails = action.payload;
        },

        clearError: (state) => {
            state.error = null;
        },

        clearService: (state) => {
            state.services = [];
            state.filteredResourceIds = null; // Reset filtered resources
            // Optionally reset filters when clearing service data? Decide based on desired UX.
            // state.calendarFilters = { ...defaultFilters };
            // state.filtersByTab = {};
            // state.activeTabKey = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // --- fetchInitialCalendarData ---
            .addCase(fetchInitialCalendarData.pending, (state) => {
                state.loadingStatus = 'loading';
                state.error = null;
            })
            .addCase(fetchInitialCalendarData.fulfilled, (state, action) => {
                state.loadingStatus = 'succeeded';
                state.resources = action.payload.resources;
                state.statusInfo = action.payload.statusInfo;
                state.serviceInfos = action.payload.serviceInfos;
                state.vehicles = action.payload.vehicles; // Store vehicle list for filter
                state.severities = action.payload.severities; // Store severities for filter
                state.suppliers = action.payload.suppliers; // Store suppliers for filter
            })
            .addCase(fetchInitialCalendarData.rejected, (state, action) => {
                state.loadingStatus = 'failed';
                state.error = action.payload;
            })
            // --- fetchServices ---
            .addCase(fetchServices.pending, (state) => {
                state.loadingStatus = 'loading';
                state.error = null;
            })
            .addCase(fetchServices.fulfilled, (state, action) => {
                state.services = action.payload;
                state.error = null;
                state.loadingStatus = 'succeeded';
            })
            .addCase(fetchServices.rejected, (state, action) => {
                state.loadingStatus = 'failed';
                state.error = action.payload;
            })

            // --- moveEvent ---
            .addCase(moveEvent.pending, (state) => {
                state.eventMovingStatus = 'loading';
                state.error = null;
            })
            .addCase(moveEvent.fulfilled, (state, action) => {
                state.eventMovingStatus = 'succeeded';
                
                // Extract updated records from response
                const updatedRecords = action.payload?.updated_records || [];
                const moveResults = action.payload?.moveResults || [];

                // Update services in state with the full updated records from backend
                if (updatedRecords.length > 0) {
                    updatedRecords.forEach((updatedRecord) => {
                        const serviceIndex = state.services.findIndex((s) => s.id === updatedRecord.id);
                        if (serviceIndex !== -1) {
                            // Update with the complete record from backend
                            state.services[serviceIndex] = {
                                ...state.services[serviceIndex],
                                ...updatedRecord
                            };
                        }
                    });
                } else if (moveResults.length > 0) {
                    // Fallback: use moveResults if updated_records not available
                    moveResults.forEach((moveResult) => {
                        const serviceIndex = state.services.findIndex((s) => s.id === moveResult.id);
                        if (serviceIndex !== -1) {
                            state.services[serviceIndex].start_date = moveResult.start_date;
                            state.services[serviceIndex].end_date = moveResult.end_date;
                        }
                    });
                }
            })
            .addCase(moveEvent.rejected, (state, action) => {
                state.eventMovingStatus = 'failed';
                state.error = action.payload;
            })
            // --- fetchYearViewData ---
            .addCase(fetchYearViewData.pending, (state) => {
                state.yearViewLoadingStatus = 'loading';
            })
            .addCase(fetchYearViewData.fulfilled, (state, action) => {
                state.yearViewLoadingStatus = 'succeeded';
                state.yearViewEvents = action.payload;
            })
            .addCase(fetchYearViewData.rejected, (state, action) => {
                state.yearViewLoadingStatus = 'failed';
                state.error = action.payload;
            });
    }
});
// --- End Slice Definition ---

// --- Export Actions ---
export const {
    updateCalendarView,
    updateDailyView,
    toggleViewMode,
    updateStoredDateModeValues,
    updateStoredMonthYearValues,
    setActiveTabKey,
    initializeTabFilters,
    removeTabFilters,
    initializeCalendarFilters,
    updateFilters,
    resetFilters,
    setPopupVisibility,
    setEventPopupDetails,
    setVehiclePopupDetails,
    setNewEventPopupDetails,
    clearError,
    clearService,
    setSearchValue,
    updateCalendarResources
} = serviceCalendarSlice.actions;
// --- End Export Actions ---

// --- Export Selectors ---
const selectServiceCalendarState = (state) => state.serviceCalendar;

// Core Data & View Selectors
export const selectServices = createSelector([selectServiceCalendarState], (calendar) => calendar.services);
export const selectCalendarResources = createSelector([selectServiceCalendarState], (calendar) => calendar.resources);
export const selectStatusInfo = createSelector([selectServiceCalendarState], (calendar) => calendar.statusInfo);
export const selectServiceInfos = createSelector([selectServiceCalendarState], (calendar) => calendar.serviceInfos);
export const selectCalendarEvents = createSelector([selectServices, selectServiceInfos], (services, serviceInfos) => {
    return transformServicesToEvents(services, serviceInfos);
});

export const selectCalendarViewStartDate = createSelector([selectServiceCalendarState], (calendar) => calendar.viewStartDate);
export const selectCalendarViewActualStartDate = createSelector([selectServiceCalendarState], (calendar) => calendar.viewActualStartDate);
export const selectCalendarIntervalMonths = createSelector([selectServiceCalendarState], (calendar) => calendar.viewIntervalMonths);
export const selectCalendarDateCompareOperator = createSelector([selectServiceCalendarState], (calendar) => calendar.viewDateCompareOperator);
export const selectCalendarIsDateMode = createSelector([selectServiceCalendarState], (calendar) => calendar.calendarIsDateMode);
export const selectCalendarStoredDateModeValues = createSelector([selectServiceCalendarState], (calendar) => calendar.calendarStoredDateModeValues);
export const selectCalendarStoredMonthYearValues = createSelector([selectServiceCalendarState], (calendar) => calendar.calendarStoredMonthYearValues);

// Daily service specific selectors
export const selectDailyViewStartDate = createSelector([selectServiceCalendarState], (calendar) => calendar.dailyViewStartDate);
export const selectDailyViewActualStartDate = createSelector([selectServiceCalendarState], (calendar) => calendar.dailyViewActualStartDate);
export const selectDailyIntervalMonths = createSelector([selectServiceCalendarState], (calendar) => calendar.dailyViewIntervalMonths);
export const selectDailyDateCompareOperator = createSelector([selectServiceCalendarState], (calendar) => calendar.dailyViewDateCompareOperator);
export const selectDailyIsDateMode = createSelector([selectServiceCalendarState], (calendar) => calendar.dailyIsDateMode);
export const selectDailyStoredDateModeValues = createSelector([selectServiceCalendarState], (calendar) => calendar.dailyStoredDateModeValues);
export const selectDailyStoredMonthYearValues = createSelector([selectServiceCalendarState], (calendar) => calendar.dailyStoredMonthYearValues);

// Filter Data Selectors (for dropdowns/options)
export const selectVehiclesForFilter = createSelector([selectServiceCalendarState], (calendar) => calendar.vehicles);
export const selectSeveritiesForFilter = createSelector([selectServiceCalendarState], (calendar) => calendar.severities);
export const selectSuppliersForFilter = createSelector([selectServiceCalendarState], (calendar) => calendar.suppliers);

// --- Selectors for Daily Service (Tabs) ---
export const selectActiveTabKey = createSelector([selectServiceCalendarState], (calendar) => calendar.activeTabKey);
export const selectFiltersByTab = createSelector([selectServiceCalendarState], (calendar) => calendar.filtersByTab);
export const selectFiltersForActiveTab = createSelector([selectFiltersByTab, selectActiveTabKey], (filtersByTab, activeTabKey) => {
    // Return default filters if active tab key is null or filters for it don't exist
    return filtersByTab[activeTabKey] || defaultFilters;
});
// --- End Selectors for Daily Service ---

// --- Selector for Service Calendar (No Tabs) ---
export const selectCalendarFilters = createSelector(
    [selectServiceCalendarState],
    (calendar) => calendar.calendarFilters || defaultFilters // Return defaults if undefined
);
// --- End Selector for Service Calendar ---

// Popup Visibility Selectors
export const selectIsStatusPopupVisible = createSelector([selectServiceCalendarState], (calendar) => calendar.popups.status);
export const selectIsServicePopupVisible = createSelector([selectServiceCalendarState], (calendar) => calendar.popups.service);
export const selectIsFilterPopupVisible = createSelector([selectServiceCalendarState], (calendar) => calendar.popups.filter);
export const selectIsYearViewPopupVisible = createSelector([selectServiceCalendarState], (calendar) => calendar.popups.yearView);
export const selectIsVehiclePopupVisible = createSelector([selectServiceCalendarState], (calendar) => calendar.popups.vehicle);

export const selectIsNewEventPopupVisible = createSelector([selectServiceCalendarState], (calendar) => calendar.popups.newEvent);

export const selectEventPopupDetails = createSelector([selectServiceCalendarState], (calendar) => calendar.eventPopupDetails);

export const selectVehiclePopupDetails = createSelector([selectServiceCalendarState], (calendar) => calendar.vehiclePopupDetails);

export const selectNewEventPopupDetails = createSelector([selectServiceCalendarState], (calendar) => calendar.newEventPopupDetails);

// Year View Specific Selectors
export const selectYearViewDetail = createSelector([selectServiceCalendarState], (calendar) => calendar.yearViewSelectedVehicleDetail);
export const selectYearViewEvents = createSelector([selectServiceCalendarState], (calendar) => calendar.yearViewEvents);
export const selectYearViewLoadingStatus = createSelector([selectServiceCalendarState], (calendar) => calendar.yearViewLoadingStatus);

export const selectFilteredEventsForYearView = createSelector([selectYearViewEvents, selectServiceInfos], (yearViewServices, serviceInfos) => {
    return transformServicesToEvents(yearViewServices, serviceInfos);
});

// --- SELECTOR for VehicleTable Data Processing ---
export const selectVehicleTableData = createSelector(
    [selectServices], // Depends on base services
    (services) => {
        let result = [];
        if (!services?.length > 0) return;
        services?.forEach((item) => {
            result.push({
                start: item.start_date ? dayjs(item.start_date).local() : null,
                end: item.completed_date ? dayjs(item.completed_date).local() : null,
                // backColor: staticService?.background_color,
                serviceId: item?.id,
                // borderColor: staticService?.border_color,
                order: item?.status_id ? Number((item.status_id + '').slice(0, 2).trim()) : 0,
                startDate: item.start_date ? dayjs(item.start_date).local().format('DD/MM/YYYY') : '—',
                time: item.start_date ? dayjs(item.start_date).local().format('hh:mm A') : '—',
                status: item?.status_id ? (item.status_id + '').slice(3)?.trim() : '—',
                plate: item?.vehicles?.[0]?.vehicle_reg ?? '–',
                vehicleId: item?.vehicles?.[0]?.vehicle_id ?? '–',
                vehicleType: item?.vehicles?.[0]?.car_name ?? '–',
                model: item?.vehicles?.[0]?.vehicle_make ?? '–',
                service: item?.service_type ?? '—',
                serviceType: item?.service_type ?? '—',
                supplier: item?.supplier_name ?? '—',
                createdAt: item?.created_at ?? '',
                updatedAt: item?.updated_at ?? '',
                statutory: !!item?.statutory,
                roadworthy: !!item?.roadworthy,
                severity: item?.severity ?? '',
                serviceScheduled: !!item?.service_scheduled,
                staticServices: item?.static_services?.[0],
                recordType: item?.record_type ?? ''
            });
        });
        result.sort((a, b) => {
            // First by start_date (null values at top)
            const dtA = a.start;
            const dtB = b.start;
            const validA = dtA && dtA.isValid();
            const validB = dtB && dtB.isValid();

            if (!validA && !validB) return 0; // Both invalid, treat as equal for date sorting
            if (!validA) return 1; // Invalid A comes after valid B
            if (!validB) return -1; // Invalid B comes after valid A

            // Compare valid dates by timestamp
            const dateComparison = dtA.valueOf() - dtB.valueOf();
            if (dateComparison !== 0) {
                return dateComparison;
            }

            // Dates/times are the same, sort by order (status)
            if (a.order !== b.order) {
                return a.order - b.order;
            }

            // Finally sort by vehicle ID
            return a.plate.localeCompare(b.plate);
        });

        return result;
    }
);

export const selectSearchValue = createSelector([selectServiceCalendarState], (calendar) => calendar.search);

// Loading/Error Selectors
export const selectCalendarLoadingStatus = createSelector([selectServiceCalendarState], (calendar) => calendar.loadingStatus);
export const selectCalendarError = createSelector([selectServiceCalendarState], (calendar) => calendar.error);

// Add new selector for event moving status
export const selectEventMovingStatus = createSelector([selectServiceCalendarState], (calendar) => calendar.eventMovingStatus);

// Add new selector for filtered calendar resources
export const selectFilteredCalendarResources = createSelector([selectServiceCalendarState], (calendar) => {
    const { resources, filteredResourceIds } = calendar;

    // If no filter is applied or filteredResourceIds is null, return all resources
    if (!filteredResourceIds || filteredResourceIds.length === 0) {
        return resources;
    }

    // Filter resources based on selected vehicle IDs
    return resources.filter((resource) => filteredResourceIds.includes(parseInt(resource.id)));
});

// Add new selector for year view vehicle ID
export const selectYearViewSelectedVehicleId = createSelector([selectServiceCalendarState], (calendar) => calendar.yearViewSelectedVehicleId);

// --- End Export Selectors ---

// --- Export Reducer ---
export default serviceCalendarSlice.reducer;
// --- End Export Reducer ---
